import React from 'react';
import { Trash2, Calendar, Clock } from 'lucide-react';
import { LoadingSpinner } from '../../../components/common/LoadingSpinner';
import { EmptyState } from '../../../components/common/EmptyState';
import { Button } from '../../../components/common/Button';
import type { BankHoliday } from '../../../utils/types';

interface BankHolidayTableProps {
  bankHolidays: BankHoliday[];
  isLoading?: boolean;
  onDelete: (id: string) => void;
  isDeleting?: Record<string, boolean>;
}

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

const getDayOfWeek = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', { weekday: 'short' });
};

const getMonthShort = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', { month: 'short' });
};

const getDayNumber = (dateString: string): string => {
  const date = new Date(dateString);
  return date.getDate().toString();
};

export const BankHolidayTable: React.FC<BankHolidayTableProps> = ({
  bankHolidays,
  isLoading,
  onDelete,
  isDeleting = {},
}) => {
  // Group by year for better organization
  const groupedByYear = React.useMemo(() => {
    const groups: Record<number, BankHoliday[]> = {};
    bankHolidays.forEach((holiday) => {
      const year = new Date(holiday.date).getFullYear();
      if (!groups[year]) {
        groups[year] = [];
      }
      groups[year].push(holiday);
    });
    // Sort years descending
    return Object.entries(groups)
      .sort(([a], [b]) => parseInt(b) - parseInt(a))
      .map(([year, holidays]) => ({
        year: parseInt(year),
        holidays: holidays.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()),
      }));
  }, [bankHolidays]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (bankHolidays.length === 0) {
    return (
      <EmptyState
        title="No bank holidays"
        description="No bank holidays have been added yet. Add holidays to exclude them from working day calculations."
        icon={<Calendar className="w-12 h-12 text-gray-400" />}
      />
    );
  }

  return (
    <div className="space-y-8">
      {groupedByYear.map(({ year, holidays }) => (
        <div key={year}>
          <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-gray-400" />
            {year}
            <span className="text-sm font-normal text-gray-500">({holidays.length} holidays)</span>
          </h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {holidays.map((holiday) => {
              const isPast = new Date(holiday.date) < new Date(new Date().setHours(0, 0, 0, 0));
              
              return (
                <div
                  key={holiday.id}
                  className={`rounded-lg border p-4 flex items-center justify-between ${
                    isPast ? 'bg-gray-50 border-gray-200' : 'bg-white border-gray-200 shadow-sm'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    {/* Date Box */}
                    <div className={`flex flex-col items-center justify-center w-14 h-14 rounded-lg border ${
                      isPast ? 'bg-gray-100 border-gray-200' : 'bg-blue-50 border-blue-200'
                    }`}>
                      <span className={`text-xs font-medium ${isPast ? 'text-gray-500' : 'text-blue-600'}`}>
                        {getMonthShort(holiday.date)}
                      </span>
                      <span className={`text-lg font-bold ${isPast ? 'text-gray-700' : 'text-blue-700'}`}>
                        {getDayNumber(holiday.date)}
                      </span>
                    </div>
                    
                    {/* Info */}
                    <div>
                      <h5 className={`font-medium ${isPast ? 'text-gray-600' : 'text-gray-900'}`}>
                        {holiday.title}
                      </h5>
                      <p className="text-sm text-gray-500">
                        {getDayOfWeek(holiday.date)}
                        {isPast && <span className="ml-2 text-xs text-gray-400">(Past)</span>}
                      </p>
                    </div>
                  </div>
                  
                  {/* Delete Button */}
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={<Trash2 className="w-4 h-4" />}
                    onClick={() => onDelete(holiday.id)}
                    isLoading={isDeleting[holiday.id]}
                    disabled={isDeleting[holiday.id]}
                    className="text-red-600 hover:text-red-800 hover:bg-red-50"
                  >
                    Delete
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default BankHolidayTable;
